import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { SidePanelEditorRow } from './SidePanelEditorRow';
import { SidePanelEditorViewModel } from '../../../viewModels/SidePanelEditorViewModel';
import { PlusIcon } from '@heroicons/react/20/solid';
import SidePanel from '../SidePanel/SidePanel';
import { OptionStyleEditor } from '../Column/OptionStyleEditor';

// Helper function to place caret at end (copied from SidePanelEditorRow)
const placeCaretAtEnd = (element) => {
    if (!element || typeof window === 'undefined') return;

    // For contentEditable elements
    if (element.isContentEditable) {
        element.focus(); // Ensure focus first
        const range = document.createRange();
        const selection = window.getSelection();
        if (!selection) return;
        range.selectNodeContents(element);
        range.collapse(false); // false means collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
    }
    // For input/textarea elements
    else if (typeof element.selectionStart === 'number') {
        element.focus(); // Ensure focus first
        // Use setTimeout to ensure focus is set before trying to set selection
        setTimeout(() => {
            try {
                const length = element.value.length;
                element.selectionStart = element.selectionEnd = length;
            } catch (e) {
                // Some input types (like 'date') might throw errors here
                console.warn("Could not set caret position for input type:", element.type, e);
            }
        }, 0);
    } else {
        // Fallback for other types if necessary
        element.focus();
    }
};

export const SidePanelEditor = observer(({ 
    viewModel, 
    itemId, 
    adjustedWidth,
    onUpdateComplete
}) => {
    // Create a new editorViewModel instance when itemId changes
    const [editorViewModel, setEditorViewModel] = useState(() => new SidePanelEditorViewModel(viewModel.sheetDataStore));
    
    // Add state to track the selected row index
    const [selectedRowIndex, setSelectedRowIndex] = useState(null); // New state for selection
    const panelRef = useRef(null); // Add a ref for the main panel container
    
    // Create/Update editorViewModel and select row when itemId changes
    useEffect(() => {
        // Clean up previous viewModel if it exists (and has a cleanup method)
        if (editorViewModel && typeof editorViewModel.cleanup === 'function') {
            editorViewModel.cleanup();
        }
        
        // Create a new viewModel for the new item
        const newViewModel = new SidePanelEditorViewModel(viewModel.sheetDataStore);
        setEditorViewModel(newViewModel);
        
        if (itemId) {
            newViewModel.setSelectedRow(itemId);
            // Automatically select the first property row if columns are available
            if (newViewModel.columns.length > 0) {
                setSelectedRowIndex(0);
            } else {
                setSelectedRowIndex(null);
            }
        } else {
            setSelectedRowIndex(null); // Reset selection if no item ID
        }
        
        // Cleanup function
        return () => {
            if (newViewModel && typeof newViewModel.cleanup === 'function') {
                 newViewModel.cleanup();
            }
        };
    }, [itemId, viewModel.sheetDataStore]); // Rerun when itemId or the main sheetDataStore changes

    // Debug logging
    useEffect(() => {
        console.log(`SidePanelEditor: Rendering with itemId=${itemId}, isLoading=${editorViewModel?.isLoading}, columns=${editorViewModel?.columns?.length}, selectedRowIndex=${selectedRowIndex}`);
        console.log("SidePanelEditor: Current columns:", editorViewModel?.columns);
    }, [itemId, selectedRowIndex, editorViewModel?.isLoading, editorViewModel?.columns]);

    // Effect to focus the first input when the item changes and editor is ready
    useEffect(() => {
        console.log(`[FocusEffect] Running. ItemId: ${itemId}, isLoading: ${editorViewModel?.isLoading}, columns: ${editorViewModel?.columns?.length}, selectedIndex: ${selectedRowIndex}`);
        // Check if we have an item, the editor isn't loading, has columns, and the first row should be selected
        if (itemId && editorViewModel && !editorViewModel.isLoading && editorViewModel.columns.length > 0 && selectedRowIndex === 0) {
            // Find the container for the selected row (index 0)
            const firstRowContainer = panelRef.current?.querySelector('[data-row-index="0"]'); // Assuming SidePanelEditorRow adds data-row-index={rowIndex}
            console.log('[FocusEffect] Conditions met. Attempting to find first row container:', firstRowContainer);
            if (firstRowContainer) {
                requestAnimationFrame(() => {
                    // Ensure firstRowContainer still exists inside requestAnimationFrame
                    if (!firstRowContainer) return;

                    const focusableInput = firstRowContainer.querySelector(
                        '.value-input-container input:not([type=checkbox]), .value-input-container textarea, .value-input-container [contenteditable="true"]'
                    );

                    console.log('[FocusEffect] Found focusable input:', focusableInput);
                    if (focusableInput) {
                        console.log(`SidePanelEditor: Focusing first row input for item ${itemId}`, focusableInput);
                        placeCaretAtEnd(focusableInput);
                    } else {
                        console.log(`SidePanelEditor: No specific input found in first row for item ${itemId}, focusing container.`);
                        firstRowContainer.focus({ preventScroll: true }); // Focus the row div itself
                    }
                });
            } else {
                console.log(`SidePanelEditor: Could not find container for first row (index 0) to focus.`);
            }
        }
    }, [itemId, editorViewModel, editorViewModel?.isLoading, editorViewModel?.columns, selectedRowIndex]); // Dependencies

    // Add keyboard navigation and interaction effect
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Ensure editorViewModel and columns are ready before handling keys
            if (!editorViewModel || editorViewModel.isLoading || !editorViewModel.columns || editorViewModel.columns.length === 0) return;

            if (!panelRef.current || !panelRef.current.contains(document.activeElement)) {
                if (selectedRowIndex !== null) {
                    return;
                }
            }

            const columns = editorViewModel?.columns || [];
            if (columns.length === 0 || selectedRowIndex === null) {
                return; // No rows or no selection
            }

            const selectedColumn = columns[selectedRowIndex];
            let nextIndex = selectedRowIndex;
            let shouldPreventDefault = false;

            // Handle Rating Input (Keys 0-5)
            if (selectedColumn?.type?.id === 'RATING' && /^[0-5]$/.test(event.key)) {
                shouldPreventDefault = true; // Prevent typing the number if focus is elsewhere
                const ratingValue = parseInt(event.key, 10);
                editorViewModel.updateField(selectedColumn.id, ratingValue);
                console.log(`SidePanelEditor: Set rating for ${selectedColumn.heading} to ${ratingValue}`);
            }
            // Handle Checkbox Toggle with Spacebar
            else if (event.key === ' ' && selectedColumn?.type?.id === 'CHECKBOX') {
                shouldPreventDefault = true;
                const currentValue = editorViewModel.getFieldValue(selectedColumn.id);
                editorViewModel.updateField(selectedColumn.id, !currentValue);
                console.log(`SidePanelEditor: Toggled checkbox for ${selectedColumn.heading}`);
            }
            // Handle Tab Navigation
            else if (event.key === 'Tab' && !event.shiftKey) {
                if (selectedRowIndex < columns.length - 1) {
                    nextIndex = selectedRowIndex + 1;
                    shouldPreventDefault = true;
                }
            } else if (event.key === 'Tab' && event.shiftKey) {
                if (selectedRowIndex > 0) {
                    nextIndex = selectedRowIndex - 1;
                    shouldPreventDefault = true;
                }
            }

            if (shouldPreventDefault) {
                event.preventDefault();
                event.stopPropagation();
                if (nextIndex !== selectedRowIndex) {
                    console.log(`SidePanelEditor: Navigating from row ${selectedRowIndex} to ${nextIndex}`);
                    setSelectedRowIndex(nextIndex);
                }
            }
        };

        if (itemId) {
            document.addEventListener('keydown', handleKeyDown, true);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [itemId, selectedRowIndex, editorViewModel, editorViewModel?.isLoading, editorViewModel?.columns]); // Added dependencies

    // Effect to select the first row index when item/columns change
    useEffect(() => {
        console.log(`[SelectIndexEffect] Running. ItemId: ${itemId}, isLoading: ${editorViewModel?.isLoading}, columns: ${editorViewModel?.columns?.length}, selectedIndex: ${selectedRowIndex}`);
        // Select first row only if an item is selected, editor is ready, and no row is currently selected
        if (itemId && editorViewModel && !editorViewModel.isLoading && editorViewModel.columns.length > 0 && selectedRowIndex === null) {
            console.log("SidePanelEditor: Item ready, selecting first row index (0).");
            setSelectedRowIndex(0);
        }
        // Reset selection if columns disappear or item ID is cleared
        else if ((!itemId || (editorViewModel && !editorViewModel.isLoading && editorViewModel.columns.length === 0)) && selectedRowIndex !== null) {
            console.log("SidePanelEditor: Resetting selected row index.");
            setSelectedRowIndex(null);
        }
    }, [itemId, editorViewModel, editorViewModel?.isLoading, editorViewModel?.columns]); // Remove selectedRowIndex from dependencies here to prevent loops

    if (!itemId) {
        return (
            <div className="p-4 text-gray-500 text-sm">
                Select an item in the table to edit its properties.
            </div>
        );
    }

    // Use isLoading from the editorViewModel
    if (editorViewModel.isLoading) { 
        return (
            <div className="p-4 text-center">
                <div className="animate-pulse flex space-x-4">
                    <div className="flex-1 space-y-4 py-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="space-y-2">
                            <div className="h-4 bg-gray-200 rounded"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                        </div>
                    </div>
                </div>
                <p className="mt-2 text-sm text-gray-500">Loading...</p>
            </div>
        );
    }

    const columns = editorViewModel?.columns || []; // Get columns from the editor VM

    // Handle case where item exists but columns are empty
    if (itemId && !editorViewModel.isLoading && columns.length === 0) {
        return (
            <div className="p-4 text-center text-sm text-gray-500">
                This item has no properties yet.
                {/* Optionally add the "Add Property" button here too */}
                <button
                   onClick={() => editorViewModel.addColumnRight()} // Add first column
                   className="mt-3 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 mx-auto" // Center button
               >
                   <PlusIcon className="h-3 w-3" />
                   Add property
               </button>
            </div>
        );
    }

    return (
        <div ref={panelRef} className="w-full">
            <div className="w-full flex flex-col items-center">
            {columns.map((column, index) => (
                <SidePanelEditorRow
                    // Ensure key includes item ID to force remount on item change if necessary
                    key={`row-${itemId}-${column.id}`}
                    column={column}
                    value={editorViewModel.getFieldValue(column.id)} // Pass current value
                    viewModel={editorViewModel}
                    isFirst={index === 0}
                    isLast={index === columns.length - 1}
                    rowIndex={index}
                    isSelected={selectedRowIndex === index} // Pass selection state
                    onSelect={() => setSelectedRowIndex(index)} // Pass selection handler
                />
            ))}
            </div>

            {/* Add Property Button */}
             <button
                onClick={() => editorViewModel.addColumnRight(columns[columns.length - 1]?.id)} // Add after last column
                className="px-4 py-4 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
            >
                <PlusIcon className="h-3 w-3" />
                Add property
            </button>
            
            {/* Add Style Editor SidePanel */}
            <SidePanel
                isOpen={!!editorViewModel.styleEditorColumnId}
                onClose={() => editorViewModel.closeColumnStyleEditor()}
                title={editorViewModel.styleEditorColumnId 
                    ? `Styles for "${columns.find(c => c.id === editorViewModel.styleEditorColumnId)?.heading || 'Property'}"` 
                    : "Styles"}
                width="100%"
                position="right"
                closeOnOutsideClick={false}
                animationDuration={300}
            >
                <div className="p-4">
                    {editorViewModel.styleEditorColumnId && (
                        <OptionStyleEditor
                            column={columns.find(c => c.id === editorViewModel.styleEditorColumnId)}
                            getColumnOptionStyles={(columnId) => editorViewModel.getColumnOptionStyles(columnId)}
                            setColumnOptionStyle={(columnId, optionValue, styleObject) => 
                                editorViewModel.setColumnOptionStyle(columnId, optionValue, styleObject)}
                            removeColumnOptionStyle={(columnId, optionValue) => 
                                editorViewModel.removeColumnOptionStyle(columnId, optionValue)}
                            onClose={() => editorViewModel.closeColumnStyleEditor()}
                        />
                    )}
                </div>
            </SidePanel>
        </div>
    );
}); 